import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Scale, BookOpen, Wallet, Layers, SlidersHorizontal, TrendingDown } from 'lucide-react';
import { ListSection, ListRow, PrimaryButton } from '../../components/RowList';

// =============================================================================
// Finance (MOD_7_FIN) — budget consolidation and control.
//
// The module that looks ACROSS sites rather than at one. Every other module in
// Mantle answers "what does my site spend"; this one answers "is the group
// budget finished, is it right, and what does it come to".
//
// It stands on ONE thing: vw_bud_gl_consolidated, which is the first place in
// the product where every module's cost exists as a single fact —
// account x cost centre x site x period x source. Everything below is a GROUP BY
// over that view, which is why the spine was built before any page was.
//
// ALL FIVE PAGES ARE LIVE. Cash Flow and the Balance Sheet were the last two
// mockups and are now built on the same spine as everything else — the cash
// flow adds depreciation back OFF THE LEDGER rather than recomputing it, and
// the balance sheet opens on the prior year's closing balances in act_gl.
// =============================================================================

const Finance = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="m-0 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Finance</h1>
          <p className="mt-2 text-base text-slate-500 dark:text-slate-400">
            Consolidation and control. Whether the budget is finished, what it comes to once every module is added
            together, and what finance adjusts on top of it.
          </p>
          <p className="mt-1.5 text-xs text-slate-400">
            Cross-site by construction — the site selector in the top bar does not narrow these pages.
          </p>
        </div>

        <ListSection title="Control">
          <ListRow
            icon={ClipboardCheck}
            title="Budget Control"
            description="Every site against every module: who has signed off, and — the part that matters — who has changed the numbers since they did. A submission that was edited after sign-off reads as done everywhere else in the product."
            actions={<PrimaryButton onClick={() => navigate('/finance/submissions')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* FINANCE'S OWN INPUTS. Everything else in this module is derived from
            the modules that produced it; these two capture what no other module
            owns — the fallback terms for a counterparty with no contract, and the
            financial policy attached to an asset. */}
        <ListSection title="Inputs">
          <ListRow
            icon={SlidersHorizontal}
            title="Working Capital"
            description="Where the three components actually come from — inventory from Manufacturing, receivables from Revenue, payables from Procurement — with how much of each rests on a real contract rather than an assumption. The days you can edit here are the fallback, not the driver."
            actions={<PrimaryButton onClick={() => navigate('/finance/working-capital')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={TrendingDown}
            title="Depreciation"
            description="What each asset cost, over how long it is written off and to which account — for assets already owned and for budgeted capex. The charge posts to the ledger, so the P&L, cash flow and balance sheet all move from this one number."
            actions={<PrimaryButton onClick={() => navigate('/finance/depreciation')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* Journals are an INPUT to the statements, not a statement — they are
            the only thing in Finance that writes to the ledger. The P&L now
            sits with the other two statements below, where a reader looking for
            "the numbers" will actually look for it. */}
        <ListSection title="Adjustments">
          <ListRow
            icon={BookOpen}
            title="Journals"
            description="Top-side adjustments over and above what the functions submitted. A separate layer that never writes into module data, so the budget is always readable as submitted plus adjustments. Must balance in every period, not just for the year — and it says so while you type."
            actions={<PrimaryButton onClick={() => navigate('/finance/journals')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* THE THREE STATEMENTS, in the order they are read: what was earned,
            what it did to cash, where it left the business standing. All three
            read the same ledger, so a journal moves all three together. */}
        <ListSection title="Statements">
          <ListRow
            icon={Scale}
            title="Profit & Loss"
            description="Every module's budgeted revenue and cost on one ledger, in the sections an accountant reads. Cost that posts NOWHERE is shown above the total rather than silently missing from it, and journals arrive as their own source so submitted and adjusted stay separable."
            actions={<PrimaryButton onClick={() => navigate('/finance/profit-and-loss')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Wallet}
            title="Cash Flow"
            description="Indirect method: profit before tax, with the costs that moved no cash added back, then the cash tied up in working capital, then investing and financing. Debtor and creditor days come from each counterparty's actual payment terms where they are on record, and the page says which lines rest on an assumption instead."
            actions={<PrimaryButton onClick={() => navigate('/finance/cash-flow')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Layers}
            title="Balance Sheet"
            description="Opening position plus budgeted movements, by section and period. Opens on the prior year's closing balances from act_gl. Each line is derived from its own driver rather than double-entered, so the balance check is a headline on the page — shown, never plugged."
            actions={<PrimaryButton onClick={() => navigate('/finance/balance-sheet')}>Open</PrimaryButton>}
          />
        </ListSection>

        <div className="mt-8 rounded-xl border border-sky-200 dark:border-sky-500/25 bg-sky-50/60 dark:bg-sky-500/5 p-4">
          <p className="m-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Where this module stands</p>
          <p className="m-0 mt-1.5 text-[11px] text-slate-600 dark:text-slate-300">
            <strong>All five pages are live.</strong> They all read one thing &mdash; the consolidated ledger
            (<code>vw_bud_gl_consolidated</code>), which now carries materials, factory labour and depreciation as well
            as opex, distribution, revenue and discounts. Its coverage check (<code>vw_bud_gl_coverage</code>) is what
            each page uses to say out loud when budgeted cost posts nowhere and is therefore missing from every figure
            on screen.
          </p>
          <p className="m-0 mt-1.5 text-[11px] text-slate-600 dark:text-slate-300">
            <strong>The opening position is real.</strong> <code>act_gl</code> carries the prior year&rsquo;s closing
            balance-sheet accounts, so the balance sheet opens on them and the cash flow starts from actual opening
            cash. The prior year&rsquo;s P&amp;L is deliberately left out &mdash; retained earnings brought forward
            already contains it.
          </p>
          <p className="m-0 mt-1.5 text-[11px] text-slate-600 dark:text-slate-300">
            <strong>What is worth knowing before you trust a figure:</strong> the balance sheet is derived line by line
            from its own drivers rather than double-entered, so it carries a residual and shows it. Interest is
            computed on the debt schedule for cash timing but charged through OPEX, so the two are reconciled on the
            cash flow rather than silently added together. Both pages name every assumption they rest on.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Finance;
